<script setup lang="ts">
import type { ContentImageCollection } from '~/models/Content/ContentImageCollection'
import { computed } from 'vue'
import { ImageCollectionMode } from '~/models/Content/ContentImageCollection'
import { useMedia } from '~/composables/useMedia'
import RichTextRenderer from '~/components/utils/RichTextRenderer.vue'
import FullscreenContent from '~/components/utils/FullscreenContent.vue'
import CopyrightButton from '~/components/utils/CopyrightButton.vue'
import ImageFadeItem from '~/components/media/ImageFadeItem.vue'
import ImageFade from '~/components/media/ImageFade.vue'
import Image from '~/components/media/Image.vue'
import Carousel from '~/components/media/Carousel.vue'

const props = defineProps<{
  content: ContentImageCollection
  embedded?: boolean
}>()

const { data: media } = useMedia(computed(() => props.content.images?.destinationContentIds ?? []))
const sorted = computed(() =>
  media.value.toSorted((a, b) => {
    const contentIds = props.content.images?.destinationContentIds ?? []
    return contentIds.indexOf(a.contentId) - contentIds.indexOf(b.contentId)
  })
)
</script>

<template>
  <div
    v-if="content.mode === ImageCollectionMode.Carousel"
    class="grid overflow-visible"
  >
    <FullscreenContent custom-trigger>
      <template #teleported="{ isFullscreen, open }">
        <Carousel
          class="col-start-1 row-start-1"
          :style="{ '--carousel-spacing': '2rem' }"
          nav="floating"
        >
          <template #default="{ index: activeIndex }">
            <div
              v-for="(image, index) in sorted"
              :key="image.contentId"
              class="grid grid-rows-[minmax(0,1fr)_max-content]"
              :class="{
                'max-h-[80vh] max-w-[80vw]': !isFullscreen,
                'w-[min(calc(60ch+var(--col-width)*2+var(--col-gap)*3),100%)]': !isFullscreen && props.embedded,
                'w-[calc(60ch+var(--col-width)*2+var(--col-gap)*3)]': !isFullscreen && !props.embedded,
                'h-screen w-screen place-items-center py-8': isFullscreen,
              }"
            >
              <div class="relative grid auto-rows-max grid-rows-1">
                <button
                  :aria-label="$t('open')"
                  class="group z-1 col-start-1 row-start-1 grid size-full cursor-pointer place-items-center focus-visible:ring"
                  @click="open"
                />
                <Image
                  :content="image"
                  class="pointer-events-none col-start-1 row-start-1 !h-full !w-full"
                  :class="{
                    'max-h-[calc(100vh-4rem)] place-self-center object-scale-down': isFullscreen,
                    'object-cover': !isFullscreen,
                  }"
                  :width="isFullscreen ? '100vw' : '400px'"
                  :use-focal-point="!isFullscreen"
                />

                <CopyrightButton
                  v-if="'copyright' in image && image.copyright"
                  class="col-start-1 row-start-1 place-self-end"
                  :copyright="image.copyright"
                  color-theme
                />

                <div
                  v-if="!isFullscreen && 'caption' in image && image.caption"
                  class="absolute bottom-0 flex w-full bg-white p-4 text-black transition-opacity"
                  :class="{ 'opacity-0': activeIndex !== index }"
                >
                  <RichTextRenderer :text="image.caption" />
                </div>
              </div>
              <div
                v-if="isFullscreen && 'caption' in image && image.caption"
                class="absolute bottom-0 flex w-full bg-white p-4 text-black transition-opacity"
                :class="{ 'opacity-0': activeIndex !== index }"
              >
                <RichTextRenderer :text="image.caption" />
              </div>
            </div>
          </template>
        </Carousel>
      </template>
    </FullscreenContent>
  </div>

  <div
    v-else-if="content.mode === ImageCollectionMode.Fade"
    class="grid"
  >
    <FullscreenContent custom-trigger>
      <template #teleported="{ isFullscreen, open }">
        <ImageFade
          nav="floating"
          :item-count="sorted.length"
          class="col-start-1 row-start-1"
          :class="{ 'h-screen w-screen place-items-center': isFullscreen }"
        >
          <template #default="{ index: activeIndex }">
            <ImageFadeItem
              v-for="(image, index) in sorted"
              :key="image.contentId"
              :is-active="index <= activeIndex"
              :is-fullscreen="isFullscreen"
              :class="{
                'h-screen w-screen place-items-center': isFullscreen,
                'pb-10': isFullscreen && 'caption' in image && image.caption,
              }"
            >
              <div
                class="group relative grid grid-rows-[minmax(0,1fr)_max-content]"
                :class="{
                  'max-h-full w-[calc(60ch+var(--col-width)*2+var(--col-gap)*3)] ': !isFullscreen,
                }"
              >
                <div class="relative grid auto-rows-max grid-rows-1">
                  <button
                    :aria-label="$t('open')"
                    class="group z-1 col-start-1 row-start-1 grid size-full cursor-pointer place-items-center focus-visible:ring"
                    @click="open"
                  />
                  <Image
                    :content="image"
                    class="pointer-events-none col-start-1 row-start-1 !h-full !w-full"
                    :class="{
                      'max-h-[calc(100vh-8rem)] max-w-[calc(100vw-8rem)] place-self-center object-scale-down': isFullscreen,
                      'object-cover': !isFullscreen,
                    }"
                    :width="isFullscreen ? '100vw' : '400px'"
                    :use-focal-point="!isFullscreen"
                  />
                  <CopyrightButton
                    v-if="'copyright' in image && image.copyright"
                    class="col-start-1 row-start-1 !hidden place-self-end group-hover:!block"
                    :copyright="image.copyright"
                    color-theme
                  />
                  <div
                    v-if="!isFullscreen && 'caption' in image && image.caption"
                    class="absolute bottom-0 w-full bg-white p-4 text-black transition-opacity"
                    :class="{ 'opacity-0': activeIndex !== index }"
                  >
                    <RichTextRenderer :text="image.caption" />
                  </div>
                </div>
              </div>
              <div
                v-if="isFullscreen && 'caption' in image && image.caption"
                class="absolute bottom-0 w-full bg-white p-4 text-black transition-opacity"
                :class="{ 'opacity-0': activeIndex !== index }"
              >
                <RichTextRenderer :text="image.caption" />
              </div>
            </ImageFadeItem>
          </template>
        </ImageFade>
      </template>
    </FullscreenContent>
  </div>

  <!-- Falls back to ImageCollectionMode.DancingImages -->
  <div
    v-else
    class="relative z-0 grid auto-cols-fr auto-rows-fr grid-cols-2 gap-2 text-base md:pb-16"
    :class="sorted.length > 6 ? 'md:grid-cols-3 lg:grid-cols-6' : 'md:grid-flow-col'"
  >
    <div
      v-for="(image, index) in sorted"
      :key="image.contentId"
      class="grid bg-white p-4 md:-mx-2"
      :class="{
        '-rotate-6': (index + 1) % 4 === 1,
        'rotate-6 lg:translate-y-16': (index + 1) % 4 === 2 && sorted.length > 6,
        'rotate-6 md:translate-y-16': (index + 1) % 4 === 2 && sorted.length <= 6,
        '-rotate-2': (index + 1) % 4 === 3,
        'rotate-2 lg:translate-y-16': (index + 1) % 4 === 0 && sorted.length > 6,
        'rotate-2 md:translate-y-16': (index + 1) % 4 === 0 && sorted.length <= 6,
      }"
      :style="{
        zIndex: index * -1,
      }"
    >
      <FullscreenContent>
        <template #default="{ isFullscreen }">
          <Image
            :content="image"
            class="col-start-1 row-start-1 !h-full max-h-[90lvh] !w-full"
            :class="isFullscreen ? 'object-contain p-4 place-self-center' : 'object-cover'"
            :width="isFullscreen ? '100vw' : '400px'"
            :use-focal-point="!isFullscreen"
          />

          <CopyrightButton
            v-if="'copyright' in image && image.copyright"
            class="col-start-1 row-start-1 place-self-end"
            :copyright="image.copyright"
          />

          <div
            v-if="'caption' in image && image.caption"
            class="row-start-2"
            :class="isFullscreen ? 'bg-white p-4' : 'mt-2'"
          >
            <RichTextRenderer :text="image.caption" />
          </div>
        </template>
      </FullscreenContent>
    </div>
  </div>
</template>
